import { isEqual } from 'lodash';
import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useGetIntegrationSettingFieldsQuery } from '@/middlewares/defaultApi';
import type { RootState } from '@/reducers/reducers';
import { selectAppSettings } from '@/selectors/selectors';
import type { IntegrationInstallationId, SettingField } from '@/types';

export const useIntegrationSettingFields = (
    orgId: string,
    appId: string,
    installationId: IntegrationInstallationId | undefined,
): { settingFields: SettingField[] | undefined; isLoadingSettingFields: boolean; isSettingFieldsError: boolean } => {
    const { data, isFetching, isError, refetch } = useGetIntegrationSettingFieldsQuery(
        { orgId, appId },
        { refetchOnMountOrArgChange: false }, // The integrations page already fetched these on mount
    );

    // Whenever there are any changes to the app's integrations, refetch the fields
    const integrations = useSelector((state: RootState) => selectAppSettings(state, appId)?.integrations);
    const previousIntegrations = useRef(integrations);
    useEffect(() => {
        if (isEqual(previousIntegrations.current, integrations)) {
            return;
        }
        previousIntegrations.current = integrations;
        refetch();
    }, [integrations, refetch]);

    return {
        settingFields: installationId ? data?.data?.[installationId] : undefined,
        isLoadingSettingFields: isFetching,
        isSettingFieldsError: isError,
    };
};
